import { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useSearchParams } from 'react-router-dom';
import { bindSelectNumericOnFocus } from './utils/selectNumericOnFocus';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AircraftProvider } from './aircraft/AircraftContext';
import Navbar from './Navbar/Navbar';
import ProtectedRoute from './ProtectedRoute';
import LoginPage from './pages/LoginPage/LoginPage';
import RegisterPage from './pages/RegisterPage/RegisterPage';
import InvitePage from './pages/InvitePage/InvitePage';
import CreateClubPage from './pages/CreateClubPage/CreateClubPage';
import HomePage from './pages/HomePage/HomePage';
import FlightsPage from './pages/FlightsPage/FlightsPage';
import StatsPage from './pages/StatsPage/StatsPage';
import BookingsPage from './pages/BookingsPage/BookingsPage';
import IssuesPage from './pages/IssuesPage/IssuesPage';
import MaintenancePage from './pages/MaintenancePage/MaintenancePage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import ProfileFlightsPage from './pages/ProfilePage/ProfileFlightsPage';
import ProfileDocumentsPage from './pages/ProfilePage/ProfileDocumentsPage';
import ProfileFinancePage from './pages/ProfilePage/ProfileFinancePage';
import ClubPage from './pages/ClubPage/ClubPage';
import AircraftPage from './pages/AircraftPage/AircraftPage';
import AircraftDocumentsPage from './pages/AircraftPage/AircraftDocumentsPage';
import TechnicalConditionPage from './pages/TechnicalConditionPage/TechnicalConditionPage';
import ModernizationPage from './pages/ModernizationPage/ModernizationPage';
import FinancePage from './pages/FinancePage/FinancePage';
import SupportPage from './pages/SupportPage/SupportPage';
import SupportNewPage from './pages/SupportPage/SupportNewPage';
import SupportTicketPage from './pages/SupportPage/SupportTicketPage';
import SupportInboxPage from './pages/SupportPage/SupportInboxPage';

function RedirectFlightsHistory() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={qs ? `/stats?${qs}` : '/stats'} replace />;
}

/** Аккаунт поддержки не состоит в клубах: ему доступны только обращения. */
function SupportShell() {
  return (
    <>
      <Navbar />
      <div className="app-main">
        <Routes>
          <Route path="/support/inbox" element={<SupportInboxPage />} />
          <Route path="/support/:ticketId" element={<SupportTicketPage />} />
          <Route path="*" element={<Navigate to="/support/inbox" replace />} />
        </Routes>
      </div>
    </>
  );
}

function AppShell() {
  const { isAuthenticated, isSupport, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (isAuthenticated && isSupport) {
    return <SupportShell />;
  }

  return (
    <AircraftProvider>
      {isAuthenticated && <Navbar />}
      <div className="app-main">
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route
          path="/club"
          element={
            <ProtectedRoute requireAircraft={false}>
              <ClubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clubs/new"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest denyTechnician>
              <CreateClubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flights"
          element={
            <ProtectedRoute requireFlyOrBook>
              <FlightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flights-history"
          element={
            <ProtectedRoute>
              <RedirectFlightsHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stats"
          element={
            <ProtectedRoute>
              <StatsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings"
          element={
            <ProtectedRoute requireFlyOrBook>
              <BookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/issues"
          element={
            <ProtectedRoute>
              <IssuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/condition"
          element={
            <ProtectedRoute>
              <TechnicalConditionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/modernization"
          element={
            <ProtectedRoute>
              <ModernizationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <ProtectedRoute denyGuest>
              <FinancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance"
          element={
            <ProtectedRoute>
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance-history"
          element={<Navigate to="/maintenance" replace />}
        />
        <Route
          path="/profile/flights"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfileFlightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/finance"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfileFinancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/documents"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfileDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId/flights"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfileFlightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId/finance"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfileFinancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId/documents"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfileDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute requireAircraft={false} denyGuest>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/aircraft/documents"
          element={
            <ProtectedRoute>
              <AircraftDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/aircraft"
          element={
            <ProtectedRoute requireAircraft={false}>
              <AircraftPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute requireAircraft={false}>
              <SupportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/new"
          element={
            <ProtectedRoute requireAircraft={false}>
              <SupportNewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/:ticketId"
          element={
            <ProtectedRoute requireAircraft={false}>
              <SupportTicketPage />
            </ProtectedRoute>
          }
        />
        </Routes>
      </div>
    </AircraftProvider>
  );
}

function App() {
  useEffect(() => bindSelectNumericOnFocus(), []);

  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

export default App;
